import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { 
  Banknote, LogOut, FileText, Clock, CheckCircle2, XCircle, AlertCircle,
  CreditCard, TrendingUp, Calendar, User, Edit3, PlusCircle, Eye, Trash2,
  Phone, MapPin, Briefcase, Building2, DollarSign, Shield, ChevronRight,
  Pencil
} from 'lucide-react';
import ClientChat from '@/pages/ClientChat';
import InstallAppButton from '@/components/InstallAppButton';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDashboard() {
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, isImpersonating, exitImpersonation } = useAuth();
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [detailApp, setDetailApp] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loanHistory, setLoanHistory] = useState([]);

  useEffect(() => {
    fetchApplications();
    axios.get(`${API_URL}/client/application-draft`, getAuthHeader())
      .then(res => setDraft(res.data))
      .catch(() => {});
    axios.get(`${API_URL}/client/loan-history`, getAuthHeader())
      .then(res => setLoanHistory(res.data || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const discardDraft = async () => {
    try {
      await axios.delete(`${API_URL}/client/application-draft`, getAuthHeader());
      setDraft(null);
      toast.success('Saved application discarded');
    } catch { toast.error('Could not discard draft'); }
  };

  const fetchApplications = async () => {
    try {
      const response = await axios.get(`${API_URL}/client/applications`, getAuthHeader());
      setApplications(response.data);
    } catch (error) {
      toast.error('Failed to load applications');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const fetchSchedule = async (appId) => {
    setScheduleLoading(true);
    try {
      const res = await axios.get(`${API_URL}/client/applications/${appId}/schedule`, getAuthHeader());
      setSchedule(res.data);
    } catch {
      setSchedule(null);
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleDocReUpload = async (e, appId, fieldKey) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    if (!allowed.includes(file.type)) {
      toast.error('Only JPG, PNG and PDF files are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File too large (max 10MB)');
      return;
    }
    setUploadingDoc(fieldKey);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const uploadRes = await axios.post(`${API_URL}/client/upload-document`, formData, {
        ...getAuthHeader(),
        headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' }
      });
      await axios.put(`${API_URL}/client/applications/${appId}/documents`, {
        [`${fieldKey}_file_id`]: uploadRes.data.file_id
      }, getAuthHeader());
      toast.success(`${file.name} uploaded successfully`);
      // Refresh application data
      const appRes = await axios.get(`${API_URL}/client/applications/${appId}`, getAuthHeader());
      setDetailApp(appRes.data);
      fetchApplications();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingDoc(null);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      submitted: { class: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock, label: 'Submitted' },
      pending: { class: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, label: 'Pending' },
      under_review: { class: 'bg-purple-100 text-purple-700 border-purple-200', icon: Eye, label: 'Under Review' },
      awaiting_documents: { class: 'bg-amber-100 text-amber-800 border-amber-300', icon: Clock, label: 'Awaiting Documents' },
      approved: { class: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: CheckCircle2, label: 'Approved' },
      declined: { class: 'bg-red-100 text-red-700 border-red-200', icon: XCircle, label: 'Declined' },
      cancelled: { class: 'bg-slate-100 text-slate-600 border-slate-200', icon: XCircle, label: 'Cancelled' },
      repaid: { class: 'bg-teal-100 text-teal-700 border-teal-200', icon: CheckCircle2, label: 'Repaid' },
    };
    const style = styles[status] || styles.submitted;
    const Icon = style.icon;
    return (
      <Badge variant="outline" className={`${style.class} flex items-center gap-1`} data-testid={`status-badge-${status}`}>
        <Icon className="w-3 h-3" />
        {style.label}
      </Badge>
    );
  };

  const canEdit = (status) => ['submitted', 'pending', 'awaiting_documents'].includes(status);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  const totalApplied = applications.reduce((sum, a) => sum + (a.applied_amount || a.loan_amount || 0), 0);
  const approvedApps = applications.filter(a => a.status === 'approved' || a.status === 'repaid');
  const totalApproved = approvedApps.reduce((sum, a) => sum + (a.loan_amount || 0), 0);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-dashboard">
      {isImpersonating && isImpersonating() && (
        <div className="bg-amber-500 text-white text-sm px-4 py-2 flex items-center justify-center gap-3 sticky top-0 z-[60]" data-testid="impersonation-banner">
          <span>Support mode — you are viewing this portal as <strong>{user?.full_name}</strong></span>
          <button
            className="bg-white/20 hover:bg-white/30 rounded-full px-3 py-0.5 text-xs font-semibold transition-colors"
            onClick={async () => { await exitImpersonation(); window.location.href = '/admin/dashboard'; }}
            data-testid="exit-impersonation-btn"
          >
            Exit client view
          </button>
        </div>
      )}
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-emerald-900 rounded-xl flex items-center justify-center">
                <Banknote className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-emerald-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                2k2day
              </span>
            </Link>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-2 text-slate-600">
                <User className="w-4 h-4" />
                <span className="font-medium">{user?.full_name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={() => navigate('/client/profile')} className="text-slate-600 hover:text-emerald-700" data-testid="profile-btn">
                <Pencil className="w-4 h-4 mr-1" /> Profile
              </Button>
              <InstallAppButton />
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-slate-600 hover:text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" /> Logout
              </Button>
            </div>
          </div>
        </div>
      </header>
      <ClientChat getAuthHeader={getAuthHeader} />

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {draft?.exists && (
          <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="draft-banner">
            <div className="flex-1">
              <p className="font-semibold text-amber-900 text-sm">You have a saved loan application</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Last saved {draft.updated_at ? new Date(draft.updated_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'recently'} — pick up where you left off to finish your details or uploads.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Link to="/apply">
                <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white rounded-full" data-testid="continue-draft-btn">
                  Continue Application
                </Button>
              </Link>
              <Button size="sm" variant="ghost" className="text-amber-700 hover:text-red-600" onClick={discardDraft} data-testid="discard-draft-btn">
                Discard
              </Button>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-emerald-900 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Welcome back, {user?.full_name?.split(' ')[0]}!
            </h1>
            <p className="text-slate-600 mt-1">Here&apos;s your loan applications overview.</p>
            <p className="text-xs text-emerald-700 mt-1.5 flex items-center gap-1" data-testid="chat-note-line">
              💬 Need anything? Use the chat box — our team replies right here in the app.
            </p>
          </div>
          <Link to="/apply">
            <Button className="bg-emerald-900 hover:bg-emerald-800 rounded-full" data-testid="new-application-btn">
              <PlusCircle className="w-4 h-4 mr-2" /> New Application
            </Button>
          </Link>
        </div>

        {/* Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="border-slate-100" data-testid="stat-total-apps">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Applications</p>
                  <p className="text-2xl font-bold text-emerald-900 tabular-nums mt-1">{applications.length}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100" data-testid="stat-total-applied">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Applied</p>
                  <p className="text-2xl font-bold text-amber-600 tabular-nums mt-1">{formatCurrency(totalApplied)}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-100" data-testid="stat-approved">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Approved</p>
                  <p className="text-2xl font-bold text-emerald-600 tabular-nums mt-1">{formatCurrency(totalApproved)}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Applications Table */}
        <Card className="border-slate-100">
          <CardHeader>
            <CardTitle className="flex items-center text-emerald-900" style={{ fontFamily: 'Manrope' }}>
              <FileText className="w-5 h-5 mr-2" /> Your Applications
            </CardTitle>
          </CardHeader>
          <CardContent>
            {applications.length === 0 ? (
              <div className="text-center py-12" data-testid="no-applications">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-4">No loan applications yet</p>
                <Link to="/apply">
                  <Button className="rounded-full bg-emerald-900 hover:bg-emerald-800">Apply for a Loan</Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="applications-table">
                  <thead>
                    <tr>
                      <th>Reference</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Total Repayment</th>
                      <th>Next Instalment</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications.map((app) => (
                      <tr key={app.id}>
                        <td className="font-mono text-xs text-slate-600" data-testid={`app-ref-${app.id}`}>{app.application_number}</td>
                        <td className="text-slate-600">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                            {formatDate(app.created_at)}
                          </div>
                        </td>
                        <td className="font-medium tabular-nums" data-testid={`app-amount-${app.id}`}>
                          {formatCurrency(app.loan_amount)}
                          {app.applied_amount && app.applied_amount !== app.loan_amount && (
                            <p className="text-[11px] text-slate-400">applied {formatCurrency(app.applied_amount)} · approved {formatCurrency(app.loan_amount)}</p>
                          )}
                        </td>
                        <td className="font-semibold text-emerald-900 tabular-nums">{formatCurrency(app.total_repayment)}</td>
                        <td data-testid={`next-instalment-${app.id}`}>
                          {app.status === 'repaid' ? (
                            <span className="text-teal-700 text-sm font-medium">Paid up ✓</span>
                          ) : app.next_repayment_date ? (
                            <div className="text-sm">
                              <p className="font-medium tabular-nums">{formatCurrency(app.amount_due || 0)}</p>
                              <p className="text-xs text-slate-500">{app.next_repayment_date}{app.instalments_total ? ` · ${app.instalments_total} instalment(s)` : ''}</p>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-sm">—</span>
                          )}
                        </td>
                        <td>{getStatusBadge(app.status)}</td>
                        <td>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-800" data-testid={`view-app-${app.id}`}
                              onClick={() => setDetailApp(app)}>
                              <Eye className="w-4 h-4 mr-1" /> View
                            </Button>
                            {canEdit(app.status) && (
                              <Link to={`/apply?edit=${app.id}`}>
                                <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-800" data-testid={`edit-app-${app.id}`}>
                                  <Edit3 className="w-4 h-4 mr-1" /> Edit
                                </Button>
                              </Link>
                            )}
                            {['submitted', 'pending', 'under_review', 'awaiting_documents'].includes(app.status) && (
                              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" data-testid={`delete-app-${app.id}`}
                                onClick={async () => {
                                  if (!window.confirm(`Delete application ${app.application_number}? This cannot be undone.`)) return;
                                  try {
                                    await axios.delete(`${API_URL}/client/applications/${app.id}`, getAuthHeader());
                                    toast.success('Application deleted');
                                    fetchApplications();
                                  } catch (err) { toast.error(err.response?.data?.detail || 'Could not delete'); }
                                }}>
                                <Trash2 className="w-4 h-4 mr-1" /> Delete
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Loan History (statement-style summary) */}
        {loanHistory.length > 0 && (
          <Card className="mt-8 border-slate-200" data-testid="loan-history-card">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <FileText className="w-5 h-5 text-emerald-700" /> Loan History
                </CardTitle>
                <Button variant="outline" size="sm" className="rounded-full" data-testid="download-statement-btn"
                  onClick={async () => {
                    try {
                      const res = await axios.get(`${API_URL}/client/loan-history/pdf`, { ...getAuthHeader(), responseType: 'blob' });
                      const url = window.URL.createObjectURL(res.data);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = '2k2day_loan_history_statement.pdf';
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success('Statement downloaded');
                    } catch { toast.error('Could not download statement'); }
                  }}>
                  Download Statement (PDF)
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="data-table" data-testid="loan-history-table">
                  <thead>
                    <tr>
                      <th>Reference number</th>
                      <th>Date</th>
                      <th>Loan amount</th>
                      <th>Instalment</th>
                      <th>Balance</th>
                      <th>Arrears</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loanHistory.map((r) => (
                      <tr key={r.reference} data-testid={`loan-history-row-${r.reference}`}>
                        <td className="font-mono text-xs text-slate-600"># {r.reference}</td>
                        <td className="text-slate-600 whitespace-nowrap">{r.date ? r.date.split('-').reverse().join('/') : '—'}</td>
                        {r.status === 'declined' || r.status === 'cancelled' ? (
                          <>
                            <td><span className="text-xs font-bold text-red-600">{r.status.toUpperCase()}</span></td>
                            <td className="text-slate-400">—</td>
                            <td className="text-slate-400">—</td>
                            <td className="text-slate-400">—</td>
                          </>
                        ) : (
                          <>
                            <td className="font-medium tabular-nums">{formatCurrency(r.loan_amount || 0)}</td>
                            <td className="tabular-nums">{formatCurrency(r.instalment || 0)}</td>
                            <td className="tabular-nums">{formatCurrency(r.balance || 0)}</td>
                            <td className={`tabular-nums ${r.arrears > 0 ? 'text-red-600 font-semibold' : ''}`}>{formatCurrency(r.arrears || 0)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Apply CTA */}
        <div className="mt-8 text-center">
          <Link to="/apply">
            <Button className="h-12 px-8 rounded-full bg-emerald-900 hover:bg-emerald-800" data-testid="apply-new-loan-btn">
              Apply for Another Loan
            </Button>
          </Link>
        </div>

        {/* Application Detail Dialog */}
        <Dialog open={!!detailApp} onOpenChange={open => !open && setDetailApp(null)}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            {detailApp && (() => {
              const a = detailApp;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="flex items-center justify-between">
                      <span className="text-emerald-900">Application {a.application_number}</span>
                      {getStatusBadge(a.status)}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 text-sm">
                    {/* Status Banner */}
                    <div className={`p-4 rounded-lg text-center ${
                      a.status === 'approved' ? 'bg-emerald-50 border border-emerald-200' :
                      a.status === 'declined' ? 'bg-red-50 border border-red-200' :
                      a.status === 'under_review' ? 'bg-purple-50 border border-purple-200' :
                      a.status === 'awaiting_documents' ? 'bg-amber-50 border border-amber-200' :
                      'bg-blue-50 border border-blue-200'
                    }`}>
                      <p className="text-lg font-bold">
                        {a.status === 'approved' && 'Your loan has been approved!'}
                        {a.status === 'declined' && 'Your application was not approved'}
                        {a.status === 'under_review' && 'Your application is under review'}
                        {a.status === 'awaiting_documents' && 'We need more documents from you — please upload them below'}
                        {a.status === 'submitted' && 'Your application has been submitted'}
                        {a.status === 'pending' && 'Your application is pending review'}
                        {a.status === 'cancelled' && 'This application was cancelled'}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        Submitted {formatDate(a.created_at)} &middot; Last updated {formatDate(a.updated_at || a.created_at)}
                      </p>
                    </div>

                    {/* Loan Summary */}
                    <div className="p-4 bg-[#0f2b4a] text-white rounded-lg">
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                        <div>
                          <span className="text-white/60 text-xs">Loan Amount</span>
                          <p className="font-bold text-lg">R{Number(a.loan_amount || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <span className="text-white/60 text-xs">Total Repayment</span>
                          <p className="font-bold text-lg text-emerald-300">R{Number(a.total_repayment || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <span className="text-white/60 text-xs">Period</span>
                          <p className="font-medium">{a.repayment_period || 30} days</p>
                        </div>
                        <div>
                          <span className="text-white/60 text-xs">Total Fees</span>
                          <p className="font-medium">R{Number(a.total_fees || 0).toLocaleString()}</p>
                        </div>
                        <div>
                          <span className="text-white/60 text-xs">Purpose</span>
                          <p className="font-medium">{a.loan_purpose || '—'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Personal Info */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><User className="w-4 h-4"/>Your Details</h4>
                      <div className="grid grid-cols-2 gap-2">
                        <ClientDetailRow label="Name" value={`${a.title || ''} ${a.first_name} ${a.last_name}`} />
                        <ClientDetailRow label="ID Number" value={a.id_number} />
                        <ClientDetailRow label="Cell" value={a.cell_number} />
                        <ClientDetailRow label="Email" value={a.email} />
                        <ClientDetailRow label="Employer" value={a.employer_name} />
                        <ClientDetailRow label="Net Income" value={a.net_income ? `R${Number(a.net_income).toLocaleString()}` : null} />
                        <ClientDetailRow label="Bank" value={a.bank_name} />
                        <ClientDetailRow label="Account" value={a.account_number} />
                      </div>
                    </div>

                    {/* Documents with Re-Upload */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><FileText className="w-4 h-4"/>Documents</h4>
                      <div className="space-y-2">
                        {[
                          { key: 'id_document', label: 'SA ID Document', fileId: a.id_document_file_id },
                          { key: 'payslip', label: 'Latest Payslip', fileId: a.payslip_file_id },
                          { key: 'bank_statement', label: 'Bank Statement', fileId: a.bank_statement_file_id },
                          { key: 'proof_of_address', label: 'Proof of Address', fileId: a.proof_of_address_file_id },
                        ].map(doc => (
                          <div key={doc.key} className={`flex items-center justify-between p-3 rounded-lg border ${doc.fileId ? 'bg-white border-emerald-200' : 'bg-white border-slate-200'}`}
                            data-testid={`client-doc-${doc.key}`}>
                            <div className="flex items-center gap-2">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${doc.fileId ? 'bg-emerald-100' : 'bg-slate-100'}`}>
                                {doc.fileId ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <FileText className="w-4 h-4 text-slate-400" />}
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-700">{doc.label}</p>
                                <p className="text-xs text-slate-400">{doc.fileId ? 'Uploaded' : 'Not uploaded'}</p>
                              </div>
                            </div>
                            <label className="cursor-pointer">
                              <input type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf"
                                onChange={e => handleDocReUpload(e, a.id, doc.key)}
                                disabled={uploadingDoc === doc.key}
                                data-testid={`reupload-${doc.key}-input`} />
                              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition cursor-pointer ${
                                doc.fileId
                                  ? 'bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100'
                                  : 'bg-emerald-900 text-white hover:bg-emerald-800'
                              }`} data-testid={`reupload-${doc.key}-btn`}>
                                {uploadingDoc === doc.key ? (
                                  <><Clock className="w-3.5 h-3.5 animate-spin" /> Uploading...</>
                                ) : doc.fileId ? (
                                  <><FileText className="w-3.5 h-3.5" /> Replace</>
                                ) : (
                                  <><PlusCircle className="w-3.5 h-3.5" /> Upload</>
                                )}
                              </span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Status History */}
                    {a.status_history?.length > 0 && (
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Clock className="w-4 h-4"/>Status Timeline</h4>
                        <div className="space-y-2">
                          {a.status_history.map((h, i) => (
                            <div key={i} className="flex items-center gap-3 text-xs">
                              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                h.status === 'approved' ? 'bg-emerald-500' :
                                h.status === 'declined' ? 'bg-red-500' :
                                h.status === 'under_review' ? 'bg-purple-500' :
                                'bg-blue-500'
                              }`}/>
                              <span className="font-medium text-slate-700 capitalize">{h.status.replace('_', ' ')}</span>
                              <span className="text-slate-400">{new Date(h.timestamp).toLocaleString('en-ZA')}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Admin Notes */}
                    {a.admin_notes?.length > 0 && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-1.5"><AlertCircle className="w-4 h-4"/>Notes from 2k2day</h4>
                        <div className="space-y-2">
                          {a.admin_notes.map((n, i) => (
                            <div key={i} className="text-sm text-amber-900 bg-white rounded-md p-3 border border-amber-100">
                              <p>{n.note}</p>
                              <p className="text-xs text-amber-600 mt-1">{new Date(n.timestamp).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Payment Schedule - for approved loans */}
                    {a.status === 'approved' && (
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-semibold text-slate-700 flex items-center gap-1.5"><CreditCard className="w-4 h-4"/>Repayment Schedule</h4>
                          {!schedule && (
                            <Button size="sm" variant="outline" onClick={() => fetchSchedule(a.id)} disabled={scheduleLoading} data-testid="load-schedule-btn">
                              {scheduleLoading ? 'Loading...' : 'View Schedule'}
                            </Button>
                          )}
                        </div>
                        {schedule && (
                          <div className="space-y-3">
                            {/* Payment Summary */}
                            <div className="grid grid-cols-3 gap-3 p-3 bg-white rounded-lg border border-slate-200">
                              <div>
                                <span className="text-xs text-slate-500">Total Due</span>
                                <p className="font-bold text-slate-800">{formatCurrency(schedule.total_due)}</p>
                              </div>
                              <div>
                                <span className="text-xs text-slate-500">Total Paid</span>
                                <p className="font-bold text-emerald-600">{formatCurrency(schedule.total_paid)}</p>
                              </div>
                              <div>
                                <span className="text-xs text-slate-500">Outstanding</span>
                                <p className="font-bold text-red-600">{formatCurrency(schedule.outstanding)}</p>
                              </div>
                            </div>
                            {/* Progress bar */}
                            <div className="h-3 bg-slate-200 rounded-full overflow-hidden">
                              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${schedule.total_due > 0 ? Math.min(100, (schedule.total_paid / schedule.total_due) * 100) : 0}%` }} />
                            </div>
                            {/* Schedule table */}
                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="border-b border-slate-200">
                                    <th className="text-left py-2 text-xs text-slate-500">#</th>
                                    <th className="text-left py-2 text-xs text-slate-500">Due Date</th>
                                    <th className="text-left py-2 text-xs text-slate-500">Amount</th>
                                    <th className="text-left py-2 text-xs text-slate-500">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {schedule.schedule.map(item => (
                                    <tr key={item.installment} className="border-b border-slate-100">
                                      <td className="py-2 text-slate-600">{item.installment}</td>
                                      <td className="py-2">{item.due_date ? new Date(item.due_date).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : 'To be confirmed'}</td>
                                      <td className="py-2 font-medium">{formatCurrency(item.amount)}</td>
                                      <td className="py-2">
                                        <Badge variant="outline" className={
                                          item.status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                                          item.status === 'partial' ? 'bg-amber-100 text-amber-700' :
                                          'bg-slate-100 text-slate-600'
                                        }>
                                          {item.status === 'paid' ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <Clock className="w-3 h-3 mr-1" />}
                                          {item.status}
                                        </Badge>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {/* Payment history */}
                            {schedule.payments?.length > 0 && (
                              <div className="mt-2">
                                <p className="text-xs font-medium text-slate-500 mb-1">Payment History</p>
                                {schedule.payments.map(p => (
                                  <div key={p.id} className="flex items-center justify-between text-xs py-1 border-b border-slate-100">
                                    <span className="text-slate-600">{p.payment_date} — {p.reference}</span>
                                    <span className="font-medium text-emerald-700">{formatCurrency(p.amount)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Edit CTA */}
                    {canEdit(a.status) && (
                      <Link to={`/apply?edit=${a.id}`}>
                        <Button className="w-full bg-emerald-900 hover:bg-emerald-800" data-testid="detail-edit-btn">
                          <Edit3 className="w-4 h-4 mr-2" /> Edit Application
                        </Button>
                      </Link>
                    )}
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </main>

      {/* Profile & password are managed on the /client/profile page */}
    </div>
  );
}

function ClientDetailRow({ label, value }) {
  return (
    <div>
      <span className="text-slate-500 text-xs">{label}</span>
      <p className="font-medium text-slate-800">{value || '—'}</p>
    </div>
  );
}
